import React, { useCallback } from 'react';
import { Modal } from 'reactstrap';
import { hideModal } from 'actions/modalActions';
import { useDispatch } from 'react-redux';
import { useModalOffset } from 'hooks/useModalOffset';
import { isModalView } from 'utils/isModalView';
import styled from 'styled-components';
import { Button, Icon, IconButton, Layout, Typography } from '@hub-mono/ui';

const StyledContainer = styled.div`
    padding-top: 20px;
    min-height: calc(100vh - 100px);
`;

const Wrapper = styled(Layout)`
    margin: 0 auto;
    max-width: 937px;
`;

const StyledImage = styled.img`
    object-fit: cover;
    object-position: top center;
    width: 100%;
`;

const BottomContentLayout = styled(Layout)`
    max-width: 450px;
`;

const multiSelectPoints = [
    'Simplify scheduling with Multi-Select tool.',
    'Rearrange bookings easily for better workflow.',
    'Flexibly adjust appointments for optimal management.',
    'Optimize operations with precise adjustments.',
    'Boost productivity by managing multiple bookings.',
];

export const LearnMoreAboutMultiSelectModal = () => {
    const dispatch = useDispatch();
    const fullScreen = isModalView();

    const offsetClassNames = useModalOffset(`rounded-0 col-sm-9 col-lg-9 col-md-8 col-xl-10 px-0`, fullScreen);

    const handleTryOut = useCallback(() => {
        dispatch(hideModal());

        if (!document) {
            return;
        }
        const button = document.querySelector('button[data-cy="scheduler-multiselect-btn"]');

        if(!button) {
            return;
        }

        button.classList.add('highlight')

        setTimeout(() => {
            button.classList.remove('highlight')
        }, 2000)

    }, [dispatch]);

    return (
        <Modal
            className="custom-modal"
            contentClassName={offsetClassNames || 'rounded-0 col-sm-12 col-lg-12 col-md-12 col-xl-12 px-0'}
            size="lg"
            isOpen
            fade={false}
            toggle={() => dispatch(hideModal())}
        >
            <Layout hAlign="flex-end">
                <Layout stack hAlign="center">
                    <IconButton icon="close" dataCy="close-feature-modal" onClick={() => dispatch(hideModal())} />
                    <Typography variant="body" size="small" color="on-surface-variant">
                        esc
                    </Typography>
                </Layout>
            </Layout>
            <StyledContainer>
                <Wrapper stack>
                    <Layout gap="--Spacing-100" vAlign="center">
                        <Layout flex="0 0 32px">
                            <Icon name="selectionPlus" size={32} />
                        </Layout>
                        <Typography variant="label" size="xl" color="on-surface-variant" prominent>
                            Streamline your scheduling with the Multi-Select tool
                        </Typography>
                    </Layout>
                    <Layout pt={46} gap={48}>
                        <Layout flex="0 1 380px" stack>
                            <StyledImage src="https://hubplanner-static.s3.eu-west-1.amazonaws.com/img/toil.png" />
                        </Layout>
                        <Layout flex="1 1 auto" stack gap={32}>
                            <Layout stack gap="--Spacing-50">
                                <Typography variant="display" size="small" prominent color="on-surface-variant">
                                    Multi Select in Hub Planner
                                </Typography>
                                <Typography variant="label" size="medium" prominent color="on-surface-variant">
                                    Drag and drop bookings using the Multi Select tool
                                </Typography>
                            </Layout>
                            <Layout stack gap="--Spacing-100">
                                {multiSelectPoints.map((item, index) => {
                                    return (
                                        <Layout key={index} gap="--Spacing-100" vAlign="center">
                                            <Icon name="arrowForward" />
                                            <Typography variant="label" size="large" prominent>
                                                {item}
                                            </Typography>
                                        </Layout>
                                    );
                                })}
                            </Layout>
                            <Layout>
                                <Button onClick={handleTryOut} endIcon="arrowForward">
                                    Try out Multi Select
                                </Button>
                            </Layout>
                        </Layout>
                    </Layout>
                    <BottomContentLayout stack gap="--Spacing-200" pt={81}>
                        <Typography variant="headline" size="small" color="on-surface-variant">
                            Enhance your scheduling experience with the Multi-Select tool
                        </Typography>
                        <Typography variant="body" size="medium" color="on-surface-variant">
                            Designed to streamline your workflow by allowing you to select multiple bookings
                            simultaneously. Easily drag and drop multiple bookings horizontally, and move a single
                            booking up or down to another resource or project, providing convenience and flexibility
                            when rescheduling or adjusting start dates.
                        </Typography>
                    </BottomContentLayout>
                </Wrapper>
            </StyledContainer>
        </Modal>
    );
};

export default LearnMoreAboutMultiSelectModal;
